import React, { useMemo, useState } from 'react';
import { Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  FilterRulesBuilder,
  getDimensionMetaMap,
  type Query,
} from '@components/filter/components/common/FilterRulesBuilder';
import { DataSource } from '@components/filter/components/utils';
import { useFilterMeta } from '@components/filter/containers/hooks';
import { CURRENT_PRODUCT_MODE, PRODUCT_MODE_MAP } from '@src/utils/constants';
import { useDimensionOptions, useMetricOptions } from '../hooks';
import { DraftCondition, DraftDimensionCondition } from '../types';
import {
  buildDimensionFields,
  conditionsToQuery,
  queryToConditions,
  type DimensionValueSource,
} from './dimensionQuery';

interface ConditionBuilderProps {
  value?: DraftCondition[];
  onChange?: (conditions: DraftCondition[]) => void;
  // Drives lazy loading of the metric meta (only when the drawer is open).
  enabled?: boolean;
}

// The filter builder's value controls only vary placeholder text by data source,
// but it is required, so map the current product to the closest source.
const DATA_SOURCE =
  CURRENT_PRODUCT_MODE === PRODUCT_MODE_MAP.APP
    ? DataSource.app
    : CURRENT_PRODUCT_MODE === PRODUCT_MODE_MAP.AI
      ? DataSource.ads
      : DataSource.video;

// A rule's conditions are one or more dimension matches (AND, via the reused
// filter builder: "Has any value" / "Equals", multi-value equals = IN on a key)
// AND/OR a group of metrics (OR within the group). At least one dimension OR
// metric is required (enforced by the form-level validator).
const ConditionBuilder = ({ value = [], onChange, enabled = true }: ConditionBuilderProps) => {
  const { dimensionOptions, productMapping } = useDimensionOptions();
  const { metricOptions, isLoading: metricsLoading } = useMetricOptions(enabled);

  // Filter meta powers dimension-value autocomplete (same source as the filter
  // builder). We match each AI-alert dimension to its filter field by the raw
  // gateway field key (`mapping.field`, which equals the filter meta value key).
  const filterMeta = useFilterMeta(DATA_SOURCE, { enabled });

  const valueSources = useMemo(() => {
    const meta = filterMeta.data;
    if (!meta) return {} as Record<string, DimensionValueSource>;

    const metaByFieldKey = getDimensionMetaMap(meta);
    const tagFieldMeta = meta.dimensions.find((d) => d.name === 'Tag')?.fields?.[0];
    const out: Record<string, DimensionValueSource> = {};

    dimensionOptions.forEach((opt) => {
      const mapping = (productMapping as Record<string, any>)?.[opt.value];
      if (!mapping) return;

      // Custom tags: values come from the shared TAG endpoint, scoped by tag key.
      if (mapping.field === 'TAG') {
        const base = tagFieldMeta?.urls?.value;
        if (base && mapping.key) {
          out[opt.value] = {
            kind: 'autocomplete',
            url: `${base}?key=${encodeURIComponent(mapping.key)}`,
            autocompleteOnly: false,
          };
        }
        return;
      }

      const fieldMeta = metaByFieldKey[mapping.field];
      if (!fieldMeta) return;
      if (fieldMeta.urls?.value) {
        out[opt.value] = {
          kind: 'autocomplete',
          url: fieldMeta.urls.value,
          autocompleteOnly: Boolean(fieldMeta.autocompletion_only),
        };
      } else if (fieldMeta.values?.length) {
        out[opt.value] = {
          kind: 'select',
          options: fieldMeta.values.map((v) => ({ label: v.name, value: v.value })),
        };
      }
    });

    return out;
  }, [filterMeta.data, dimensionOptions, productMapping]);

  const fields = useMemo(
    () => buildDimensionFields(dimensionOptions, valueSources),
    [dimensionOptions, valueSources],
  );

  const selectedMetrics = useMemo(
    () => value.filter((c) => c.type === 'metric').map((c: any) => c.metricKey as string),
    [value],
  );

  // Seeded once from the incoming dimension conditions, then the builder owns its
  // own query state so partially-filled rows (e.g. an "Equals" row before a value
  // is entered) survive the conditions round-trip instead of being dropped.
  const [query, setQuery] = useState<Query>(() =>
    conditionsToQuery(value.filter((c) => c.type === 'dimension') as DraftDimensionCondition[]),
  );

  const emit = (nextQuery: Query, metricKeys: string[]) => {
    const dims = queryToConditions(nextQuery);
    const metrics: DraftCondition[] = metricKeys.map((metricKey) => ({
      type: 'metric',
      metricKey,
    }));
    onChange?.([...dims, ...metrics]);
  };

  const handleQueryChange = (nextQuery: Query) => {
    setQuery(nextQuery);
    emit(nextQuery, selectedMetrics);
  };

  const handleMetricsChange = (metricKeys: string[]) => emit(query, metricKeys);

  return (
    <div>
      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
        Dimensions
        <Tooltip title='Add one or more dimensions (AND). "Has any value" matches when the alert root cause carries the dimension; "Equals" matches specific value(s); "Contains" matches when the value contains the text (multiple values on one dimension match any of them).'>
          <InfoCircleOutlined style={{ marginLeft: 6 }} />
        </Tooltip>
      </label>
      <FilterRulesBuilder
        dataSource={DATA_SOURCE}
        fields={fields}
        query={query}
        showAddGroupButton={false}
        onChange={handleQueryChange}
      />

      <label style={{ fontWeight: 600, margin: '16px 0 8px', display: 'block' }}>
        Metrics
        <Tooltip title="The rule matches when the alert's metric is any of the selected metrics (OR).">
          <InfoCircleOutlined style={{ marginLeft: 6 }} />
        </Tooltip>
      </label>
      <Select
        mode="multiple"
        showSearch
        allowClear
        style={{ width: '100%', maxWidth: 420 }}
        placeholder="Select one or more metrics (optional)"
        value={selectedMetrics}
        onChange={handleMetricsChange}
        options={metricOptions}
        optionFilterProp="label"
        loading={metricsLoading}
      />
    </div>
  );
};

export default ConditionBuilder;
